import { apiClient } from '@api/apiClient';
import { Box } from '@atgfr8/atg-ui-tailwind';
import { InformationIcon } from '@atgfr8/icons';
import { Combobox, ToastType } from '@components';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { setCustomerInfo } from '@store/ltl/store';
import {
  CustomerDetails,
  customerStore,
  isCreditLimitExceeded,
  loadCustomerInfo,
  orderContainsLTLLoad,
  orderStore,
  setCustomerStore,
  updateLoadPropertyAtIndex,
  updateOrderState,
  validateFieldForOrder,
} from '@store/orders';
import { defaultCustomerDetails } from '@store/orders/defaultVals';
import { ICustomerContact } from '@typeDefinitions/contactTypes';
import { customerEditable, handleToast, printError } from '@utils/utils';
import { Setter, Show } from 'solid-js';

import classes from './classes';

type Props = {
  setCustomerWarnings: Setter<string[] | null>;
};

const CustomerSelection = (props: Props) => {
  const cls = classes.customerInfo;

  const handleSelect = async (item: ICustomerContact) => {
    try {
      await loadCustomerInfo(item.id);
    } catch (error) {
      printError(error);
      handleToast(ToastType.Error, 'Unable to load customer info');
    }

    updateOrderState({ customerId: item.id });
    setCustomerInfo(item.id, item.name);
    if (orderStore.order.loads.length > 0) {
      updateLoadPropertyAtIndex({ customerId: item.id });
    }
    await validateFieldForOrder('customerId', 'customerId');
  };

  return (
    <Box class="flex gap-4 my-[16px] items-center" id="instructions-and-costs">
      <Box
        class={`${cls.customerContainer} ${isFeatureFlagEnabled(FeatureFlags.feature_order_block_save_on_neg_credit_limit)() && !orderContainsLTLLoad() && isCreditLimitExceeded() ? 'rounded [&_.MuiTextField-root]:bg-[#FDEDED]!' : ''}`}
      >
        {orderStore.order.loads.some((x) => x.hideCustomerName) ? (
          <span class={cls.customerFieldInfo}>
            {orderStore.order.customer?.name}
          </span>
        ) : (
          <Combobox<ICustomerContact>
            label="Customer Name"
            value={customerStore.customer.name}
            required={false}
            fetchData={async (query: string) => {
              const response = await apiClient.get<
                string,
                { value?: ICustomerContact[] } | ICustomerContact[]
              >(
                `customer/autocomplete?term=${encodeURIComponent(query.trimStart())}`,
              );
              const items =
                'value' in response && response.value
                  ? response.value
                  : (response as ICustomerContact[]);
              return items.slice(0, 50);
            }}
            getOptionLabel={(item: ICustomerContact) => item.name}
            onSelect={(item: ICustomerContact | null) => {
              if (item) {
                void handleSelect(item);
                return;
              }
              updateOrderState({
                customerId: null,
                customerContactId: undefined,
                owner: undefined,
                metadata: undefined,
              });
              setCustomerStore({
                customer: defaultCustomerDetails as unknown as CustomerDetails,
                loading: false,
              });
              props.setCustomerWarnings(null);
            }}
            error={orderStore.orderFormError?.['customerId']?.[0] ?? null}
            disabled={
              orderStore.order.hideCustomerName ||
              !customerEditable() ||
              orderStore.isReadOnly
            }
          />
        )}
        <Show when={customerStore.customer.thirdParty}>
          <span class={cls.customerFieldInfo}>
            <InformationIcon style={cls.infoIconStyle} />
            This customer has 3rd Party Billing
          </span>
        </Show>
      </Box>
    </Box>
  );
};
export default CustomerSelection;
